// Slim runtime schema with phantom typing. No dependencies. Purpose: validate event
// payloads at the wire boundary and surface TS types from a single declaration.
//
// Not a general validation lib. No `safeParse`, no error aggregation, no async, no
// transforms. Throws SchemaError on first failure. BYO Zod via a `{ parse, serialize, _t }`
// adapter if you need more.

export class SchemaError extends Error {
  readonly path: string;
  constructor(path: string, message: string) {
    super(path.length === 0 ? message : `at ${path}: ${message}`);
    this.name = 'SchemaError';
    this.path = path;
  }
}

export interface Schema<T> {
  readonly _t: T;
  parse(value: unknown, path?: string): T;
  serialize(value: T): unknown;
}

export type Infer<S> = S extends Schema<infer T> ? T : never;

function expect(cond: boolean, path: string, msg: string): asserts cond {
  if (!cond) throw new SchemaError(path, msg);
}

function isPlainObject(value: unknown): boolean {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function describe(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  return typeof value;
}

export function string(): Schema<string> {
  return {
    _t: '',
    parse(value, path = '') {
      expect(typeof value === 'string', path, `expected string, got ${typeof value}`);
      return value;
    },
    serialize(v) { return v; },
  };
}

export function number(): Schema<number> {
  return {
    _t: 0,
    parse(value, path = '') {
      expect(typeof value === 'number' && Number.isFinite(value), path, `expected finite number, got ${describe(value)}`);
      return value;
    },
    serialize(v) { return v; },
  };
}

export function boolean(): Schema<boolean> {
  return {
    _t: false,
    parse(value, path = '') {
      expect(typeof value === 'boolean', path, `expected boolean, got ${typeof value}`);
      return value;
    },
    serialize(v) { return v; },
  };
}

// Optional-key derivation: a field whose schema admits undefined becomes a `?:` key in the
// inferred object type so consumers can omit it entirely (not just write `field: undefined`).
type IsOptionalSchema<S> = S extends Schema<infer T> ? (undefined extends T ? true : false) : false;
type ObjectShape<F> =
  & { [K in keyof F as IsOptionalSchema<F[K]> extends true ? never : K]: Infer<F[K]> }
  & { [K in keyof F as IsOptionalSchema<F[K]> extends true ? K : never]?: Infer<F[K]> };

// Generic object: every field must be present unless its schema is `optional(...)`.
// An explicit `undefined` for an optional field is preserved (the parser writes the key
// even when the value is undefined) so consumers that branch on `key in obj` work right.
export function object<F extends Record<string, Schema<unknown>>>(
  fields: F,
): Schema<ObjectShape<F>> {
  const fieldKeys = Object.keys(fields);
  return {
    _t: undefined as unknown as ObjectShape<F>,
    parse(value, path = '') {
      expect(isPlainObject(value), path, `expected object, got ${describe(value)}`);
      const v = value as Record<string, unknown>;
      const out: Record<string, unknown> = {};
      for (const key of fieldKeys) {
        const fieldSchema = fields[key]!;
        const childPath = path.length === 0 ? key : `${path}.${key}`;
        out[key] = fieldSchema.parse(v[key], childPath);
      }
      return out as ObjectShape<F>;
    },
    serialize(v) {
      const out: Record<string, unknown> = {};
      for (const key of fieldKeys) {
        out[key] = fields[key]!.serialize((v as Record<string, unknown>)[key]);
      }
      return out;
    },
  };
}

export function array<S extends Schema<unknown>>(item: S): Schema<Infer<S>[]> {
  return {
    _t: [] as Infer<S>[],
    parse(value, path = '') {
      expect(Array.isArray(value), path, `expected array, got ${describe(value)}`);
      const out: Infer<S>[] = [];
      for (let i = 0; i < value.length; i++) {
        out.push(item.parse(value[i], `${path}[${i}]`) as Infer<S>);
      }
      return out;
    },
    serialize(v) { return v.map((x) => item.serialize(x)); },
  };
}

export function record<V extends Schema<unknown>>(value: V): Schema<Record<string, Infer<V>>> {
  return {
    _t: {} as Record<string, Infer<V>>,
    parse(input, path = '') {
      expect(isPlainObject(input), path, `expected record, got ${describe(input)}`);
      const v = input as Record<string, unknown>;
      // Object.create(null): downstream wire-side adversaries who control keys can't write
      // `constructor` or other built-in names onto the parsed object.
      const out: Record<string, Infer<V>> = Object.create(null) as Record<string, Infer<V>>;
      for (const k of Object.keys(v)) {
        out[k] = value.parse(v[k], path.length === 0 ? k : `${path}.${k}`) as Infer<V>;
      }
      return out;
    },
    serialize(v) {
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(v)) out[k] = value.serialize(v[k]!);
      return out;
    },
  };
}

export function optional<S extends Schema<unknown>>(inner: S): Schema<Infer<S> | undefined> {
  return {
    _t: undefined as Infer<S> | undefined,
    parse(value, path = '') {
      if (value === undefined) return undefined;
      return inner.parse(value, path) as Infer<S>;
    },
    serialize(v) { return v === undefined ? undefined : inner.serialize(v); },
  };
}

export function nullable<S extends Schema<unknown>>(inner: S): Schema<Infer<S> | null> {
  return {
    _t: null as Infer<S> | null,
    parse(value, path = '') {
      if (value === null) return null;
      return inner.parse(value, path) as Infer<S>;
    },
    serialize(v) { return v === null ? null : inner.serialize(v); },
  };
}

export function enumOf<L extends readonly [string, ...string[]]>(...values: L): Schema<L[number]> {
  const set = new Set<string>(values);
  return {
    _t: values[0],
    parse(value, path = '') {
      expect(typeof value === 'string', path, `expected string for enum, got ${typeof value}`);
      expect(set.has(value), path, `expected one of [${values.join(', ')}], got ${value}`);
      return value as L[number];
    },
    serialize(v) { return v; },
  };
}

// Brand: nominal typing without runtime cost. Used for typed IDs (`t.brand(t.string(), 'TodoId')`).
// Caveat: structurally distinct only at the type level; at runtime a branded string is a
// string. Don't rely on `TodoId.parse(...)` to reject "the wrong kind of string".
declare const BRAND: unique symbol;
export type Branded<T, B extends string> = T & { readonly [BRAND]: B };

export function brand<T, B extends string>(base: Schema<T>, _brand: B): Schema<Branded<T, B>> {
  return {
    _t: '' as unknown as Branded<T, B>,
    parse(value, path) { return base.parse(value, path) as Branded<T, B>; },
    serialize(v) { return base.serialize(v as T); },
  };
}

export const t = {
  string,
  number,
  boolean,
  object,
  array,
  record,
  optional,
  nullable,
  enumOf,
  brand,
};
